import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Switch } from 'antd';
import styled from 'styled-components';
import { noBrushSelector } from '@store/trends/birdChart/selectors';
import { summaryWidgetsSelector } from '@store/summaryWidgets/selector';
import { networkRequestSelector } from '@store/networkRequest/selector';
import { setDrillDown, setSyncedAutoRefreshState } from '@store/networkRequest/actions';
import { setSlice } from '@store/trends/birdChart/actions';
import useFirstRender from '@src/hooks/utils/useFirstRender';
import { isAppModule } from '@utils/constants';
import { SUMMARY_PANEL_TYPE } from '@components/top-widget-panel/constants';
import useSetupLiveMode from './hooks/useSetupLiveMode';

const AutoRefreshComponent = styled.div`
  display: flex;
  margin: 0 10px;
  align-items: center;
  justify-content: space-between;
`;

const AutoRefreshSpan = styled.span`
  padding-right: 5px;
  font-size: 14px;
`;

const AutoRefreshToggle = () => {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const noBrushSelected = useSelector(noBrushSelector);
  const { type } = useSelector(summaryWidgetsSelector);
  const { drillDown } = useSelector(networkRequestSelector);
  const dispatch = useDispatch();

  const first = useFirstRender();
  const { refreshHandler } = useSetupLiveMode({ autoRefresh });

  const isAppNetwork = isAppModule && type === SUMMARY_PANEL_TYPE.NETWORK && drillDown;

  useEffect(() => {
    // disable auto refesh when there is an active slice on trends charts
    if (!first && !noBrushSelected) {
      setAutoRefresh(false);
      if (isAppNetwork) {
        dispatch(setSyncedAutoRefreshState(false));
      }
    }
  }, [noBrushSelected, first, isAppNetwork, dispatch]);

  useEffect(() => {
    // disable auto refesh when there is drill down
    if (!first && isAppNetwork) {
      setAutoRefresh(false);
      dispatch(setSyncedAutoRefreshState(false));
    }
  }, [dispatch, first, isAppNetwork]);

  // unmounting the component, clear the auto refresh state
  useEffect(() => {
    return () => {
      if (isAppNetwork) {
        dispatch(setSyncedAutoRefreshState(true));
      }
    };
  }, [dispatch, isAppNetwork]);

  const onChange = () => {
    // enabling auto refresh
    if (!autoRefresh) {
      dispatch(setSlice([0, 100], [0, 0])); // clear slice
      dispatch(setDrillDown(false)); // clear drill down
      refreshHandler(); // immediate refresh
    }
    setAutoRefresh(!autoRefresh);
    if (isAppNetwork) {
      dispatch(setSyncedAutoRefreshState(!autoRefresh));
    }
  };

  return (
    <AutoRefreshComponent>
      <AutoRefreshSpan>{'Auto Refresh:'}</AutoRefreshSpan>
      <Switch size="small" checked={autoRefresh} onChange={onChange} />
    </AutoRefreshComponent>
  );
};

export default AutoRefreshToggle;
